import React, { useContext, useEffect, useRef, useMemo, useState } from 'react'
import { Accordion, AccordionContent, AccordionItem, AccordionTitle, Cell, Grid } from 'react-foundation'
import { RegionCheckboxFieldGroup, SelectFieldGroup } from '../Common/Input/FormFields'
import { useFormikContext } from 'formik';
import UserContext from '../../UserContext';
import { SaveEvent, SelectEvent } from '../Common/AnalyticsEvents';

type ServiceAreaProps = {
    regions?: Record<string, Record<string, string[]>>
    zipToLatLng?: Record<string, [number, number]>
    states: string[];
}


const SERVICE_RADIUS_OPTIONS = [
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '75', label: '75' },
    { value: '0', label: 'Statewide' }
];

// ServiceArea expects to be rendered inside a Formik context (see ProviderPreferenceForm.tsx)
// Do NOT wrap this in another Formik provider, as it will break parent form integration.

type FormValues = {
    serviceRadius: string;
    serviceRadiusZipCode?: string;
    serviceArea: Record<string, Record<string, string[]>>;
};

const buildAllCheckedFromRegions = (source: Record<string, Record<string, string[]>> | undefined) => {
    if (!source) return {} as Record<string, Record<string, string[]>>;
    return Object.entries(source).reduce((result, [region, counties]) => {
        result[region] = Object.entries(counties).reduce((countyResult, [county, zips]) => {
            countyResult[county] = [...zips];
            return countyResult;
        }, {} as Record<string, string[]>);
        return result;
    }, {} as Record<string, Record<string, string[]>>);
};

const hasAnySelectedZip = (serviceArea: Record<string, Record<string, string[]>> | undefined) => {
    if (!serviceArea) return false;
    return Object.values(serviceArea).some(region => Object.values(region).some(zips => zips.length > 0));
};

const pruneServiceAreaToVisible = (
    serviceArea: Record<string, Record<string, string[]>> | undefined,
    visibleRegions: Record<string, Record<string, string[]>> | undefined
) => {
    if (!serviceArea || !visibleRegions) return {} as Record<string, Record<string, string[]>>;

    return Object.entries(serviceArea).reduce((pruned, [region, counties]) => {
        const visibleCounties = visibleRegions[region];
        if (!visibleCounties) return pruned;

        const keptCounties = Object.entries(counties).reduce((countyResult, [county, zips]) => {
            const visibleZips = visibleCounties[county];
            if (!visibleZips) return countyResult;

            const keptZips = zips.filter(zip => visibleZips.includes(zip));
            if (keptZips.length > 0) countyResult[county] = keptZips;
            return countyResult;
        }, {} as Record<string, string[]>);

        if (Object.keys(keptCounties).length > 0) pruned[region] = keptCounties;
        return pruned;
    }, {} as Record<string, Record<string, string[]>>);
};

const countUniqueZips = (source: Record<string, Record<string, string[]>> | undefined) => {
    if (!source) return 0;

    const unique = new Set<string>();
    Object.values(source).forEach((counties) => {
        if (!counties || typeof counties !== 'object') return;
        Object.values(counties).forEach((zips) => {
            if (!Array.isArray(zips)) return;
            zips.forEach((zip) => unique.add(zip.toString().slice(0, 5)));
        });
    });

    return unique.size;
};

const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const toRad = (x: number) => x * Math.PI / 180;
    const R = 3958.8; // Radius of Earth in miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const ServiceArea: React.FC<ServiceAreaProps & { userZipCode?: string }> = ({ regions, zipToLatLng = {}, userZipCode, states }) => {
    const user = useContext(UserContext);
    const { values, setFieldValue } = useFormikContext<FormValues>();
    const prevServiceRadius = useRef<string | undefined>(values.serviceRadius);
    const radiusZipInput = values.serviceRadiusZipCode ?? (userZipCode ? userZipCode.slice(0, 5) : '');

    // Always use only the first 5 digits of the zip code for lookup
    const normalizedRadiusZip = radiusZipInput.replace(/\D/g, '').slice(0, 5);
    const hasRadiusZip = normalizedRadiusZip.length === 5;
    const hasZipCoordinates = hasRadiusZip && !!zipToLatLng[normalizedRadiusZip];
    const canApplyRadius = values.serviceRadius === '0' || hasZipCoordinates;
    const [showAccordion, setShowAccordion] = useState<string[]>([])
    

    // Memoize filtered regions based on radius and from-zip value
    const filteredRegions = useMemo(() => {
        if (!regions || values.serviceRadius === '0') return regions;
        if (!hasZipCoordinates) return regions;

        const userCoords = zipToLatLng[normalizedRadiusZip];
        if (!userCoords) return regions;

        const radius = parseInt(values.serviceRadius, 10);
        const filtered: Record<string, Record<string, string[]>> = {};
        Object.entries(regions).forEach(([region, counties]) => {
            const filteredCounties: Record<string, string[]> = {};
            Object.entries(counties).forEach(([county, zips]) => {
                const zipsInRadius = zips.filter(zip => {
                    const coords = zipToLatLng[zip.toString().slice(0, 5)];
                    if (!coords) return false;
                    return haversineDistance(userCoords[0], userCoords[1], coords[0], coords[1]) <= radius;
                });
                if (zipsInRadius.length > 0) {
                    filteredCounties[county] = zipsInRadius;
                }
            });
            if (Object.keys(filteredCounties).length > 0) {
                filtered[region] = filteredCounties;
            }
        });
        return filtered;
    }, [regions, normalizedRadiusZip, hasZipCoordinates, values.serviceRadius, zipToLatLng]);

    useEffect(() => {
        // Only reconcile serviceArea when radius actually changes.
        if (prevServiceRadius.current === values.serviceRadius) return;

        const previousRadius = prevServiceRadius.current;
        prevServiceRadius.current = values.serviceRadius;
        if (previousRadius === undefined) return;

        const radiusLabel = values.serviceRadius === '0' ? 'Statewide' : `${values.serviceRadius} miles`;
        SaveEvent(SelectEvent(user, 'Service Area', `Service Radius: ${radiusLabel}`));

        if (values.serviceRadius === '0') {
            // Statewide always selects all available zips.
            setFieldValue('serviceArea', buildAllCheckedFromRegions(regions));
            return;
        }

        if (!hasZipCoordinates) {
            // Without a valid from-zip we cannot calculate distance, so preserve current selection.
            return;
        }

        const visible = filteredRegions;
        if (!visible || Object.keys(visible).length === 0) {
            setFieldValue('serviceArea', {});
            return;
        }

        const isRadiusIncreasing = parseInt(values.serviceRadius, 10) > parseInt(previousRadius, 10);
        const hasSelection = hasAnySelectedZip(values.serviceArea);

        if (!hasSelection || isRadiusIncreasing) {
            // For first-time selection and larger mile ranges, select all zips that are currently in-range.
            setFieldValue('serviceArea', buildAllCheckedFromRegions(visible));
            return;
        }

        // For tighter mile ranges, keep only selected zips that remain visible.
        setFieldValue('serviceArea', pruneServiceAreaToVisible(values.serviceArea, visible));
    }, [values.serviceRadius, regions, setFieldValue, filteredRegions, values.serviceArea, hasZipCoordinates, user]);

    if (!filteredRegions) return null;
    const hasSelectableCounties = Object.values(filteredRegions).some(region => Object.keys(region).length > 0)
    const visibleZipCount = countUniqueZips(filteredRegions)
    const selectedZipCount = countUniqueZips(values.serviceArea)

    return (
        <Grid className='card service-area-card'>
            <Cell small={12}>
                <div className='preferences-section-header service-area-header'>
                    <div className='service-area-title-row'>
                        <span className='service-area-icon'>
                            <i className='fa-regular fa-location-dot'></i>
                        </span>
                        <div>
                            <h4>Service Area Configuration</h4>
                            <h6>Define your service area and coverage zones</h6>
                        </div>
                    </div>
                </div>

                <div className='service-radius-panel'>
                    <h6 className="margin-none">
                        <b>Service Radius (miles)</b>
                    </h6>
                    <p className="font-small color-black service-radius-help">Set your desired service radius to receive notifications for jobs near your business address.</p>
                    <div className='service-radius-zip-row'>
                        <label htmlFor='serviceAreaFromZip' className='service-radius-zip-label'>From ZIP Code</label>
                        <input
                            id='serviceAreaFromZip'
                            className='service-radius-zip-input'
                            type='text'
                            value={radiusZipInput}
                            maxLength={5}
                            inputMode='numeric'
                            pattern='[0-9]*'
                            placeholder='Enter 5-digit ZIP'
                            onChange={(e) => setFieldValue('serviceRadiusZipCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                            onBlur={() => {
                                if (normalizedRadiusZip.length === 5)
                                    SaveEvent(SelectEvent(user, 'Service Area', `From_ZIP_${normalizedRadiusZip}`))
                            }}
                        />
                    </div>
                    {values.serviceRadius !== '0' && !hasRadiusZip ? (
                        <p className='service-radius-warning'>Enter a 5-digit ZIP code to apply mileage-based filtering.</p>
                    ) : null}
                    {values.serviceRadius !== '0' && hasRadiusZip && !hasZipCoordinates ? (
                        <p className='service-radius-warning'>ZIP code coordinates were not found. Mileage filtering is not applied.</p>
                    ) : null}
                    {values.serviceRadius !== '0' && canApplyRadius ? (
                        <p className='service-radius-ready'>Mileage filtering is active from ZIP {normalizedRadiusZip}.</p>
                    ) : null}
                    <div className='half-width service-radius-select'>
                        <SelectFieldGroup
                            name='serviceRadius'
                            noLabel={true}
                            options={SERVICE_RADIUS_OPTIONS}
                        />
                    </div>
                </div>

                <div className='service-locator-panel'>
                    <div className='service-locator-header'>
                        <h5>Region, County, and Zip Code Selections</h5>
                        <p className="font-small color-black">You can narrow down by region, county, and specific zip codes. Jobs inside your radius will only appear if the area remains selected.</p>
                    </div>
                <Grid>
                    <Cell small={12} large={12} className='eight-column'>
                        {hasSelectableCounties ? (
                            states.length < 2 
                            ?
                            <RegionCheckboxFieldGroup
                                name="serviceArea"
                                title="Zip Code Specific Selection"
                                options={filteredRegions}
                                onCountyChange={(county, checked) => SaveEvent(SelectEvent(user, 'Service Area', `County_${county}_${checked ? 'Selected' : 'Deselected'}`))}
                                onZipChange={(zip, checked) => SaveEvent(SelectEvent(user, 'Service Area', `ZIP_${zip}_${checked ? 'Selected' : 'Deselected'}`))}
                            />
                            :
                            <Accordion>
                                <AccordionItem>
                                    <AccordionTitle onClick= {() =>{setShowAccordion(showAccordion.includes("OH") ? showAccordion.filter(r => r !== "OH") : ["OH", ...showAccordion])}}>
                                        <span className='title-container'>
                                        <h5 className='title'>
                                            Ohio
                                        </h5>
                                        <i className={`fa-regular service-area-caret fa-2xl ${showAccordion.includes("OH") ? 'fa-caret-up' : 'fa-caret-down'}`}></i>
                                        </span>
                                    </AccordionTitle>
                                    <AccordionContent isActive={showAccordion.includes("OH")}>
                                         <RegionCheckboxFieldGroup
                                            name="serviceArea"
                                            title="Zip Code Specific Selection"
                                            options={filteredRegions}
                                            state="OH"
                                            onCountyChange={(county, checked) => SaveEvent(SelectEvent(user, 'Service Area', `County_${county}_OH_${checked ? 'Selected' : 'Deselected'}`))}
                                            onZipChange={(zip, checked) => SaveEvent(SelectEvent(user, 'Service Area', `ZIP_${zip}_OH_${checked ? 'Selected' : 'Deselected'}`))}
                                        />
                                    </AccordionContent>
                                </AccordionItem>
                                 <AccordionItem>
                                    <AccordionTitle onClick= {() =>{setShowAccordion(showAccordion.includes("MI") ? showAccordion.filter(r => r !== "MI") : ["MI", ...showAccordion])}}>
                                        <span className='title-container'>
                                        <h5 className='title'>
                                            Michigan
                                        </h5>
                                        <i className={`fa-regular service-area-caret fa-2xl ${showAccordion.includes("MI") ? 'fa-caret-up' : 'fa-caret-down'}`}></i>
                                        </span>
                                    </AccordionTitle>
                                    <AccordionContent isActive={showAccordion.includes("MI")}>
                                         <RegionCheckboxFieldGroup
                                            name="serviceArea"
                                            title="Zip Code Specific Selection"
                                            options={filteredRegions}
                                            state="MI"
                                            onCountyChange={(county, checked) => SaveEvent(SelectEvent(user, 'Service Area', `County_${county}_MI_${checked ? 'Selected' : 'Deselected'}`))}
                                            onZipChange={(zip, checked) => SaveEvent(SelectEvent(user, 'Service Area', `ZIP_${zip}_MI_${checked ? 'Selected' : 'Deselected'}`))}
                                        />
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        ) : (
                            <p className='font-small color-gray'>No county and zip configuration is available for this state yet.</p>
                        )}
                    </Cell>
                </Grid>
                </div>
            </Cell>
        </Grid>
    );
}

export default ServiceArea
